import { useState } from 'react';
import type { ApprovedItem, SentItem, ToSendItem } from '../types';
import { money } from '../utils';
import { AUTOPILOT_SENT_THIS_WEEK, isSentDecided } from '../store';

// ---- Sent (§9) ----
const SENT_BADGE: Record<SentItem['status'], { label: string; cls: string }> = {
  waiting: { label: 'Waiting', cls: 'b-info' },
  late: { label: 'Late', cls: 'b-warn' },
  asked_more: { label: 'Asked for more', cls: 'b-warn' },
  denied: { label: 'Denied', cls: 'b-bad' },
};
// Urgency fallback for rows that tie on "needs action".
const SENT_STATUS_RANK: Record<SentItem['status'], number> = {
  denied: 0, asked_more: 1, late: 2, waiting: 3,
};

export function SentTab({ rows, onReview, onOpen }: { rows: SentItem[]; onReview: (id: string) => void; onOpen: (id: string) => void }) {
  const [closedOpen, setClosedOpen] = useState(false);
  const [query, setQuery] = useState('');
  if (rows.length === 0) {
    return (
      <div className="pa-wide">
        <div className="pa-empty"><p className="pa-empty-title">No pre-auths waiting for an answer.</p></div>
      </div>
    );
  }
  const q = query.trim().toLowerCase();
  const matches = (r: SentItem) =>
    q === '' ||
    r.patient.toLowerCase().includes(q) ||
    r.payerShort.toLowerCase().includes(q) ||
    r.code.toLowerCase().includes(q) ||
    r.procedure.toLowerCase().includes(q);
  const visible = rows.filter(matches);
  const closed = visible.filter((r) => (r.closed ?? false) || isSentDecided(r.id));
  const active = visible
    .filter((r) => !((r.closed ?? false) || isSentDecided(r.id)))
    .sort((a, b) => {
      // Items that need a person (a Decide or Review action) come first, so a
      // denial/request never sits below a waiting row (Oct 17 fix: Karen ≠ above Jorge).
      const ar = a.action ? 0 : 1;
      const br = b.action ? 0 : 1;
      if (ar !== br) return ar - br;
      const sr = SENT_STATUS_RANK[a.status] - SENT_STATUS_RANK[b.status];
      if (sr !== 0) return sr;
      return a.appointmentDisplay.localeCompare(b.appointmentDisplay);
    });

  const render = (r: SentItem) => {
    const badge = SENT_BADGE[r.status];
    return (
      <tr key={r.id} className="pa-row-open" onClick={() => { onOpen(r.id); }} style={{ cursor: 'pointer' }}>
        <td>
          <div style={{ fontWeight: 600 }}>{r.patient}</div>
          <div className="pa-meta">{r.payerShort} · {r.code} {r.procedure}</div>
        </td>
        <td>{r.sentDisplay}</td>
        <td style={{ color: 'var(--fg2)', maxWidth: 360 }}>{r.sentence}</td>
        <td>{r.appointmentDisplay}</td>
        <td className="ta-right">
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            <span className={`pa-badge ${badge.cls}`}>{badge.label}</span>
            {r.action === 'decide' && !isSentDecided(r.id) ? (
              <span className="pa-badge b-warn">Needs a decision</span>
            ) : null}
            {r.action === 'review_resend' ? (
              <button type="button" className="pa-btn pa-btn-sm" onClick={(e) => { e.stopPropagation(); onReview(r.id); }}>Review and resend</button>
            ) : null}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="pa-wide">
      <section className="pa-card" aria-labelledby="pa-sent-title">
        <div className="pa-card-head">
          <h2 className="pa-h2" id="pa-sent-title">Waiting for the payer</h2>
          <span className="pa-meta">ELVA checks each one until the payer answers. Anything that needs you is at the top.</span>
        </div>
        <div className="pa-search-wrap">
          <label className="pa-vh" htmlFor="pa-sent-search">Search sent pre-auths</label>
          <input
            id="pa-sent-search"
            className="pa-search"
            placeholder="Search patient, payer or reference"
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
          />
        </div>
        {q !== '' && active.length === 0 ? (
          <div className="pa-empty"><p className="pa-empty-title">Nothing matches “{query}”.</p></div>
        ) : null}
        <div className="pa-tbl-wrap">
          <table className="pa-tbl">
            <thead>
              <tr><th>Patient</th><th>Sent</th><th>What’s happening</th><th>Appointment</th><th className="ta-right">Status</th></tr>
            </thead>
            <tbody>{active.map(render)}</tbody>
          </table>
        </div>
        {closed.length > 0 ? (
          <div className="pa-closed">
            <button type="button" className="pa-btn pa-btn-ghost pa-btn-sm" aria-expanded={closedOpen} onClick={() => { setClosedOpen((v) => !v); }}>
              {closedOpen ? '▾' : '▸'} Closed · {closed.length}
            </button>
            {closedOpen ? (
              <div className="pa-tbl-wrap"><table className="pa-tbl"><tbody>{closed.map(render)}</tbody></table></div>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

// ---- Approved (§10) ----
const APPROVED_BADGE: Record<ApprovedItem['state'], { label: string; cls: string }> = {
  expires_first: { label: 'Expires first', cls: 'b-warn' },
  not_booked: { label: 'No visit booked', cls: 'b-warn' },
  cancelled: { label: 'Visit cancelled', cls: 'b-warn' },
  booked: { label: 'Booked', cls: 'b-info' },
  used: { label: 'Used', cls: 'b-ok' },
};
const APPROVED_ORDER: ApprovedItem['state'][] = ['expires_first', 'not_booked', 'cancelled', 'booked', 'used'];

export function ApprovedTab({ rows, onReattach }: { rows: ApprovedItem[]; onReattach: (id: string) => void }) {
  const [query, setQuery] = useState('');
  if (rows.length === 0) {
    return (
      <div className="pa-wide">
        <div className="pa-empty"><p className="pa-empty-title">No approved pre-auths waiting to be used.</p></div>
      </div>
    );
  }
  const q = query.trim().toLowerCase();
  const visible = rows.filter(
    (r) =>
      q === '' ||
      r.patient.toLowerCase().includes(q) ||
      r.payerShort.toLowerCase().includes(q) ||
      r.reference.toLowerCase().includes(q),
  );
  const sorted = [...visible].sort((a, b) => APPROVED_ORDER.indexOf(a.state) - APPROVED_ORDER.indexOf(b.state));
  return (
    <div className="pa-wide">
      <section className="pa-card" aria-labelledby="pa-app-title">
        <div className="pa-card-head">
          <h2 className="pa-h2" id="pa-app-title">Approved</h2>
          <span className="pa-meta">ELVA adds the reference number to the claim when the treatment is done. Anything that needs you is at the top.</span>
        </div>
        <div className="pa-search-wrap">
          <label className="pa-vh" htmlFor="pa-app-search">Search approved pre-auths</label>
          <input
            id="pa-app-search"
            className="pa-search"
            placeholder="Search patient, payer or reference"
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
          />
        </div>
        {q !== '' && sorted.length === 0 ? (
          <div className="pa-empty"><p className="pa-empty-title">Nothing matches “{query}”.</p></div>
        ) : null}
        <div className="pa-tbl-wrap">
          <table className="pa-tbl">
            <thead>
              <tr><th>Patient</th><th>Reference</th><th className="ta-right">Payer’s estimate</th><th>Valid until</th><th>Appointment</th><th>What’s happening</th><th className="ta-right">Status</th></tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const badge = APPROVED_BADGE[a.state];
                return (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.patient}</div>
                      <div className="pa-meta">{a.payerShort} · {a.code} {a.procedure}</div>
                    </td>
                    <td className="num">{a.reference}</td>
                    <td className="ta-right num">{money(a.estimate)}</td>
                    <td>{a.validUntil}</td>
                    <td>{a.appointmentDisplay}</td>
                    <td style={{ color: 'var(--fg2)', maxWidth: 320 }}>{a.sentence}</td>
                    <td className="ta-right">
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <span className={`pa-badge ${badge.cls}`}>{badge.label}</span>
                        {a.state === 'cancelled' ? (
                          <button type="button" className="pa-btn pa-btn-sm" onClick={() => { onReattach(a.id); }}>Rebooked — attach it</button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <section className="pa-card">
        <p style={{ margin: 0, color: 'var(--fg2)' }}>
          A pre-auth is the payer’s estimate on the day it was issued, not a guarantee. It can change if the patient’s
          coverage or remaining maximum changes before the visit.
        </p>
      </section>
    </div>
  );
}

// ---- Autopilot summary (§8) ----
export function AutopilotSummary({ held, onReview, onSent }: { held: ToSendItem[]; onReview: (id: string) => void; onSent: () => void }) {
  return (
    <div className="pa-wide">
      <section className="pa-card pa-summary">
        <div>
          <div className="pa-sum-label">Sent by ELVA this week</div>
          <div className="pa-sum-value num">{AUTOPILOT_SENT_THIS_WEEK} pre-auths</div>
        </div>
        <div>
          <div className="pa-sum-label">Held for you</div>
          <div className="pa-sum-value num">{held.length}</div>
        </div>
        <button type="button" className="pa-src pa-sum-link" style={{ textDecoration: 'underline' }} onClick={onSent}>
          See what ELVA sent
        </button>
      </section>
      <section className="pa-card" aria-labelledby="pa-held-title">
        <div className="pa-card-head">
          <h2 className="pa-h2" id="pa-held-title">Why these are waiting for you</h2>
        </div>
        <div className="pa-tbl-wrap">
          <table className="pa-tbl">
            <thead>
              <tr><th>Patient</th><th>Why ELVA held it</th><th>Appointment</th><th className="ta-right">Treatment</th><th className="ta-right">Review</th></tr>
            </thead>
            <tbody>
              {held.map((it) => (
                <tr key={it.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{it.patient}</div>
                    <div className="pa-meta">{it.payer.short} · {it.procedures[0]?.code} {it.procedureGroupId === 'crowns' ? 'crown' : 'implant'}</div>
                  </td>
                  <td style={{ color: 'var(--fg2)' }}>{it.reasonLine}</td>
                  <td>{it.appointmentDisplay.split(',').slice(0, 2).join(',')}</td>
                  <td className="ta-right num" style={{ fontWeight: 600 }}>{money(it.treatment)}</td>
                  <td className="ta-right">
                    <button type="button" className="pa-btn pa-btn-sm" onClick={() => { onReview(it.id); }}>Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
